import React from "react";
import { useLocation } from "react-router-dom";
import { useInstallPrompt } from "@/hooks/useInstallPrompt";
import { Download, X, Share, Plus } from "lucide-react";

// Routes on which we deliberately never show the PWA install banner.
// Customer feedback in particular should be a zero-friction one-tap flow —
// showing an install nag on top of it hurts response rates.
const HIDDEN_PATH_PREFIXES = ["/site/contact"];

/**
 * Bottom-sheet style install banner.
 * - Android / Desktop Chrome / Edge: shows native install button (uses beforeinstallprompt).
 * - iOS Safari: shows the "tap Share → Add to Home Screen" instructions.
 * - Already installed (standalone PWA): renders nothing.
 * - Dismissed: hidden for 7 days.
 */
export default function InstallPrompt() {
  const {
    canInstall, showIOSHint, alreadyInstalled, dismissed,
    promptInstall, dismiss,
  } = useInstallPrompt();
  const { pathname } = useLocation();

  if (HIDDEN_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"))) return null;
  if (alreadyInstalled || dismissed) return null;
  if (!canInstall && !showIOSHint) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed bottom-3 left-3 right-3 sm:left-auto sm:right-4 sm:bottom-4 sm:max-w-sm z-[60]
                 bg-[#022C22] text-white border border-emerald-400/30 rounded-2xl shadow-2xl
                 backdrop-blur-xl"
      role="dialog"
      aria-label="Install AEOIBCMS"
    >
      <div className="flex items-start gap-3 p-4">
        <div className="h-11 w-11 shrink-0 rounded-xl bg-amber-500 text-emerald-950 flex items-center justify-center font-display font-bold text-xl">
          A
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-sm leading-tight">
            Install AEOIBCMS on your device
          </div>
          {canInstall ? (
            <p className="text-xs text-emerald-100/75 mt-1 leading-relaxed">
              Add this app to your home screen for full-screen access, offline support and faster startup.
            </p>
          ) : (
            <p className="text-xs text-emerald-100/75 mt-1 leading-relaxed">
              On iPhone, tap{" "}
              <Share size={11} className="inline align-text-bottom" />{" "}
              <span className="font-semibold text-amber-300">Share</span>, then{" "}
              <Plus size={11} className="inline align-text-bottom" />{" "}
              <span className="font-semibold text-amber-300">Add to Home Screen</span>.
            </p>
          )}
          {canInstall && (
            <button
              data-testid="pwa-install-button"
              onClick={promptInstall}
              className="mt-3 inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-emerald-950
                         text-xs font-semibold px-3.5 py-1.5 rounded-full transition-colors"
            >
              <Download size={13} /> Install app
            </button>
          )}
        </div>
        <button
          type="button"
          aria-label="Dismiss install banner"
          data-testid="pwa-install-dismiss"
          onClick={dismiss}
          className="text-emerald-200/60 hover:text-white -m-1 p-1 rounded-md hover:bg-emerald-900/60"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
